import {
  Banknote,
  Check,
  FastForward,
  Layers,
  PackageOpen,
  ShieldCheck,
  Sparkles,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { PackHorse, PackPurchase } from "@/api";
import { Button, HorsePortrait, money, TierStars } from "@/components/ui";
import { packLabel } from "@/features/racing/venue";
import { useTheme } from "@/theme/ThemeProvider";

import type { RipTier } from "./quick-rip/presentation";
import { playCashSound, playKeepCue } from "./quick-rip/audio";
import { runCashout } from "./quick-rip/cashout";
import { createQuickRipFitter } from "./quick-rip/fitToScreen";
import { useQuickRipController } from "./quick-rip/useQuickRipController";
import { useInstantCash } from "./useInstantCash";

export function QuickRip({
  purchases,
  initialIndex = 0,
  instant,
  onClose,
  onViewStable,
  onIndexChange,
  onBuyAnother,
  onFinished,
  repeatPriceCents = null,
}: {
  purchases: PackPurchase[];
  initialIndex?: number;
  instant: boolean;
  onClose: () => void;
  onViewStable: () => void;
  onIndexChange?: (index: number) => void;
  onBuyAnother?: () => void;
  onFinished?: () => void;
  repeatPriceCents?: number | null;
}) {
  const { theme } = useTheme();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const revealRef = useRef<HTMLElement>(null);
  const cardsWrapRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const brandLabel = theme.brand.logoAlt.toUpperCase();
  const controller = useQuickRipController({
    purchases,
    initialIndex,
    instant,
    onIndexChange,
  });
  const {
    index,
    purchase,
    presentation,
    phase,
    fanStarted,
    fanLanded,
    slots,
    revealed,
    displayedValue,
    busy,
    glowRef,
    flashRef,
    packRef,
    popIntro,
    revealCard,
    revealRemaining,
    nextPack,
    setSlotRef,
  } = controller;
  const {
    cashedByShareId,
    decisionsByShareId,
    busyShareId,
    bulkBusy,
    keepShare,
    cashShare,
    cashBatch,
    cashError,
  } = useInstantCash();
  const [sweeping, setSweeping] = useState(false);
  const [displayedReceiveByPosition, setDisplayedReceiveByPosition] = useState<
    Record<string, number>
  >({});
  const [ripAgainArmed, setRipAgainArmed] = useState(false);
  const cardRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const receiveRef = useRef<HTMLElement>(null);
  const reducedMotion = useMemo(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const decisionFor = (horse: PackHorse) =>
    horse.shareId == null ? "keep" : decisionsByShareId[horse.shareId];
  const keepCount = purchase.contents.filter(
    (horse) => decisionFor(horse) === "keep",
  ).length;
  const cashCount = purchase.contents.filter(
    (horse) => decisionFor(horse) === "cash",
  ).length;
  const undecided = presentation.order
    .map((contentIndex) => purchase.contents[contentIndex])
    .filter((horse) => decisionFor(horse) == null);
  const allDecided = undecided.length === 0;
  const actionsBusy = busyShareId != null || bulkBusy || sweeping;
  const displayedReceive = displayedReceiveByPosition[purchase.positionId] ?? 0;
  const addDisplayedReceive = (cents: number) =>
    setDisplayedReceiveByPosition((current) => ({
      ...current,
      [purchase.positionId]: (current[purchase.positionId] ?? 0) + cents,
    }));

  const animateCash = async (horse: PackHorse, paidCents: number) => {
    const origin =
      horse.shareId != null ? cardRefs.current[horse.shareId] : null;
    const target = receiveRef.current;
    if (!origin || !target) {
      addDisplayedReceive(paidCents);
      return;
    }
    await runCashout({
      origin,
      target,
      amountCents: paidCents,
      packPriceCents: purchase.priceCents,
      reducedMotion,
      onStep: addDisplayedReceive,
    });
  };

  const cashHorse = async (horse: PackHorse) => {
    if (horse.shareId == null || actionsBusy) return;
    const result = await cashShare(horse.shareId);
    await animateCash(horse, result.paidCents);
  };

  const keepHorse = (horse: PackHorse) => {
    if (horse.shareId == null || actionsBusy) return;
    keepShare(horse.shareId);
    playKeepCue();
  };

  const keepAll = () => {
    if (actionsBusy) return;
    for (const horse of undecided) {
      if (horse.shareId != null) keepShare(horse.shareId);
    }
    playKeepCue();
  };

  const sellAllAnimated = async () => {
    if (actionsBusy) return;
    setSweeping(true);
    try {
      for (const horse of undecided) {
        if (horse.shareId == null || horse.instantQuoteCents == null) {
          if (horse.shareId != null) keepShare(horse.shareId);
          continue;
        }
        const result = await cashShare(horse.shareId);
        await animateCash(horse, result.paidCents);
      }
    } finally {
      setSweeping(false);
    }
  };

  const sellAllFast = async () => {
    if (actionsBusy) return;
    const shareIds = undecided.flatMap((horse) =>
      horse.shareId != null && horse.instantQuoteCents != null
        ? [horse.shareId]
        : [],
    );
    for (const horse of undecided) {
      if (horse.shareId != null && horse.instantQuoteCents == null) {
        keepShare(horse.shareId);
      }
    }
    if (shareIds.length === 0) return;
    const items = await cashBatch(shareIds);
    if (items.length > 0) {
      playCashSound();
      addDisplayedReceive(
        items.reduce((total, item) => total + item.paidCents, 0),
      );
      receiveRef.current?.animate?.(
        [
          { transform: "scale(1)" },
          { transform: "scale(1.2)" },
          { transform: "scale(1)" },
        ],
        { duration: 260, easing: "cubic-bezier(.34,1.56,.64,1)" },
      );
    }
  };
  const lastUnrevealed =
    purchase.contents.length - revealed.size === 1
      ? purchase.contents.findIndex(
          (_, contentIndex) => !revealed.has(contentIndex),
        )
      : -1;
  const isLastPack = index >= purchases.length - 1;

  useEffect(() => {
    const appRoot = document.getElementById("root");
    const isPortalled = Boolean(
      appRoot && dialogRef.current && !appRoot.contains(dialogRef.current),
    );
    const chrome = isPortalled
      ? []
      : [
          ...document.querySelectorAll<HTMLElement>(
            ".app-header, .app-footer, .v2-header, .v2-sidebar, .v2-mobile-nav, .v2-shell__footer",
          ),
        ];
    const rootWasInert = appRoot?.hasAttribute("inert") ?? false;
    const chromeWasInert = chrome.map((element) =>
      element.hasAttribute("inert"),
    );
    if (isPortalled) appRoot?.setAttribute("inert", "");
    chrome.forEach((element) => element.setAttribute("inert", ""));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() =>
      closeRef.current?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [
        ...dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]):not([tabindex="-1"]), a[href], input:not([disabled]), select:not([disabled])',
        ),
      ];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (isPortalled && !rootWasInert) appRoot?.removeAttribute("inert");
      chrome.forEach((element, elementIndex) => {
        if (!chromeWasInert[elementIndex]) element.removeAttribute("inert");
      });
    };
  }, [onClose]);

  useEffect(() => {
    if (phase === "summary" && isLastPack) onFinished?.();
  }, [isLastPack, onFinished, phase]);

  useLayoutEffect(() => {
    const fitter = createQuickRipFitter({
      stage: () => dialogRef.current,
      region: () => revealRef.current,
      wrap: () => cardsWrapRef.current,
      row: () => cardsRef.current,
      cardCount: () => purchase.contents.length,
    });
    return fitter.destroy;
  }, [index, phase, purchase.contents.length]);

  return (
    <div
      className="quick-rip quick-rip--production"
      data-fan-landed={fanLanded}
      data-fan-started={fanStarted}
      data-phase={phase}
      ref={dialogRef}
    >
      <div className="quick-rip__atmosphere" aria-hidden>
        <div className="quick-rip__stage-glow" ref={glowRef} />
        <div className="quick-rip__grid" />
        <div className="quick-rip__vignette" />
      </div>
      <div className="quick-rip__flash" ref={flashRef} />

      <header className="quick-rip__header">
        <div>
          <PackageOpen aria-hidden />
          <span>
            <small>QUICK RIP</small>
            <strong>
              Pack {index + 1} / {purchases.length}
            </strong>
          </span>
        </div>
        <div className="quick-rip__pager" aria-label="Pack queue">
          {purchases.map((pack, packIndex) => (
            <i
              className={packIndex === index ? "is-active" : ""}
              key={pack.positionId}
            />
          ))}
        </div>
        <button
          aria-label="Close pack opening"
          className="quick-rip__close"
          onClick={onClose}
          ref={closeRef}
          type="button"
        >
          <X aria-hidden />
        </button>
      </header>

      <main
        className="quick-rip__scene"
        onPointerDown={() => {
          if (phase === "intro") popIntro();
        }}
      >
        <section
          className={`quick-rip__intro ${
            phase === "intro" && !fanStarted ? "is-active" : ""
          }`}
          aria-hidden={phase !== "intro" || fanStarted}
        >
          <button
            aria-label="Open pack now"
            className="quick-rip__pack"
            onClick={popIntro}
            ref={packRef}
            tabIndex={phase === "intro" ? 0 : -1}
            type="button"
          >
            <div className="quick-rip__pack-foil" />
            <div className="quick-rip__pack-brand">
              <Sparkles aria-hidden />
              <small>{brandLabel}</small>
              <strong>HORSE RACING</strong>
              <span>{packLabel(purchase)}</span>
            </div>
            <div className="quick-rip__pack-seal">
              <Zap aria-hidden />5
            </div>
            <div className="quick-rip__pack-ripline">
              <i />
              <span>RIP TO OPEN</span>
              <i />
            </div>
          </button>
          <div className="quick-rip__intro-copy">
            <strong>YOUR STABLE AWAITS</strong>
            <span>Auto-opening · tap to accelerate</span>
          </div>
        </section>

        <section
          className={`quick-rip__reveal ${
            phase !== "intro" || fanStarted ? "is-active" : ""
          }`}
          aria-hidden={phase === "intro" && !fanStarted}
          ref={revealRef}
        >
          <div className="quick-rip__counter">
            <span>Your pull</span>
            <strong>{money(displayedValue)}</strong>
            <small>
              {revealed.size} / {purchase.contents.length} revealed
            </small>
          </div>

          <div className="quick-rip__cards-wrap" ref={cardsWrapRef}>
            <div
              className={`quick-rip__cards quick-rip__cards--${phase}`}
              aria-label="Quick Rip horse reveals"
              ref={cardsRef}
            >
              {presentation.order.map((contentIndex, displayIndex) => {
                const horse = purchase.contents[contentIndex];
                const visual = slots[contentIndex];
                const isRevealed = revealed.has(contentIndex);
                const cashedCents =
                  horse.shareId != null
                    ? cashedByShareId[horse.shareId]
                    : undefined;
                const cashable =
                  isRevealed &&
                  visual.phase === "revealed" &&
                  horse.shareId != null &&
                  horse.instantQuoteCents != null &&
                  cashedCents == null;
                const decision = decisionFor(horse);
                return (
                  <div className="rip-card-slot" key={horse.horseId}>
                    <button
                      aria-label={
                        isRevealed
                          ? `${horse.name}, ${visual.visibleTier}`
                          : `Reveal sealed horse ${displayIndex + 1}`
                      }
                      className={[
                        "rip-card",
                        "is-motion-owned",
                        visual.faceVisible && "is-face-up",
                        visual.phase === "sweat" && "is-sweating",
                        visual.phase === "upgrading" && "is-upgrading",
                        visual.rainbow && "is-rainbow",
                        isRevealed && "is-revealed",
                        cashedCents != null && "is-cashed",
                        lastUnrevealed === contentIndex &&
                          contentIndex === presentation.hitIndex &&
                          "is-last-hit",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      data-tier={
                        visual.faceVisible ? visual.visibleTier : "sealed"
                      }
                      disabled={phase !== "reveal" || isRevealed || busy}
                      onClick={() => void revealCard(contentIndex)}
                      ref={(element) => {
                        setSlotRef(displayIndex, element);
                        if (horse.shareId != null) {
                          cardRefs.current[horse.shareId] = element;
                        }
                      }}
                      type="button"
                    >
                      <div className="rip-card__inner">
                        <div className="rip-card__back">
                          <div className="rip-card__back-orbit" />
                          <Sparkles aria-hidden />
                          <strong>HORSE RACING</strong>
                          <span>{brandLabel}</span>
                          <small>Tap to reveal</small>
                        </div>
                        <div className="rip-card__front">
                          <HorseRipFace
                            final={visual.phase === "revealed"}
                            horse={horse}
                            tier={visual.visibleTier}
                            upgrading={visual.phase === "upgrading"}
                          />
                        </div>
                      </div>
                      <div className="rip-card__frame" aria-hidden />
                      <div className="rip-card__sparks" aria-hidden />
                      {visual.phase === "sweat" ? (
                        <span className="rip-card__sweat-label">
                          <Zap aria-hidden /> HOLD ON
                        </span>
                      ) : null}
                      {lastUnrevealed === contentIndex &&
                      contentIndex === presentation.hitIndex &&
                      !isRevealed ? (
                        <span className="rip-card__hit-hint">
                          THE HIT REMAINS
                        </span>
                      ) : null}
                      {cashedCents != null ? (
                        <span className="rip-card__cashed-badge">
                          <Banknote aria-hidden size={13} /> CASHED{" "}
                          {money(cashedCents)}
                        </span>
                      ) : null}
                    </button>
                    {cashable && phase === "summary" ? (
                      <div className="rip-card__decision">
                        <button
                          aria-pressed={decision === "keep"}
                          className={decision === "keep" ? "is-selected" : ""}
                          disabled={actionsBusy}
                          onClick={() => keepHorse(horse)}
                          type="button"
                        >
                          <Check aria-hidden size={13} />
                          Keep
                        </button>
                        <button
                          aria-label={`Cash ${horse.name} for ${money(
                            horse.instantQuoteCents ?? 0,
                          )}`}
                          aria-pressed={decision === "cash"}
                          disabled={actionsBusy}
                          onClick={() => void cashHorse(horse)}
                          type="button"
                        >
                          <Banknote aria-hidden size={13} />
                          {busyShareId === horse.shareId
                            ? "Cashing…"
                            : `Cash ${money(horse.instantQuoteCents ?? 0)}`}
                        </button>
                      </div>
                    ) : isRevealed &&
                      visual.phase === "revealed" &&
                      phase === "summary" ? (
                      <span className="rip-card__cash rip-card__cash--kept">
                        {cashedCents != null
                          ? "Sold to the house"
                          : decision === "keep"
                            ? "Kept in your stable"
                            : "No cash offer"}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            aria-hidden={phase !== "reveal" || !fanLanded}
            className="quick-rip__reveal-actions"
            data-visible={phase === "reveal" && fanLanded}
          >
            <span>Reveal in any order</span>
            <button
              className="quick-rip__fast"
              disabled={busy || phase !== "reveal" || !fanLanded}
              onClick={() => void revealRemaining()}
              tabIndex={phase === "reveal" && fanLanded ? 0 : -1}
              type="button"
            >
              <FastForward aria-hidden size={14} />
              Reveal remaining
            </button>
          </div>

          <div
            aria-hidden={phase !== "summary"}
            className="quick-rip__summary"
            data-visible={phase === "summary"}
          >
            <div className="quick-rip__decision-dock">
              <div className="quick-rip__decision-stats">
                <span>
                  KEEP <b>{keepCount}</b>
                </span>
                <span>
                  CASH <b>{cashCount}</b>
                </span>
                <span>
                  RECEIVE{" "}
                  <b data-rip-receive ref={receiveRef}>
                    {money(displayedReceive)}
                  </b>
                </span>
              </div>
              <div className="quick-rip__decision-swap">
                <div
                  aria-hidden={allDecided}
                  className="quick-rip__decision-bulk"
                  data-visible={!allDecided}
                >
                  <button
                    disabled={actionsBusy || allDecided}
                    onClick={keepAll}
                    tabIndex={allDecided ? -1 : 0}
                    type="button"
                  >
                    <Check aria-hidden size={14} />
                    Keep all
                  </button>
                  <button
                    disabled={actionsBusy || allDecided}
                    onClick={() => void sellAllAnimated()}
                    tabIndex={allDecided ? -1 : 0}
                    type="button"
                  >
                    <Banknote aria-hidden size={14} />
                    {sweeping ? "Cashing…" : "Sell all"}
                  </button>
                  <button
                    disabled={actionsBusy || allDecided}
                    onClick={() => void sellAllFast()}
                    tabIndex={allDecided ? -1 : 0}
                    type="button"
                  >
                    <Layers aria-hidden size={14} />
                    {bulkBusy ? "Settling…" : "Cash all fast"}
                  </button>
                </div>
                <div
                  aria-hidden={!allDecided}
                  className="quick-rip__decision-primary"
                  data-visible={allDecided}
                >
                  <Button
                    disabled={!allDecided || actionsBusy}
                    onClick={isLastPack ? onViewStable : () => void nextPack()}
                    tabIndex={allDecided ? 0 : -1}
                    variant="secondary"
                  >
                    {isLastPack ? "Continue to stable" : "Rip next pack"}
                  </Button>
                  {isLastPack && onBuyAnother ? (
                    <Button
                      disabled={!allDecided || actionsBusy}
                      onClick={() => {
                        if (!ripAgainArmed) {
                          setRipAgainArmed(true);
                          return;
                        }
                        onBuyAnother();
                      }}
                      tabIndex={allDecided ? 0 : -1}
                    >
                      {ripAgainArmed
                        ? `Confirm Rip Again${
                            repeatPriceCents !== null
                              ? ` · ${money(repeatPriceCents)}`
                              : ""
                          }`
                        : "Rip Again"}
                    </Button>
                  ) : isLastPack ? (
                    <Button
                      disabled={!allDecided || actionsBusy}
                      onClick={onClose}
                      tabIndex={allDecided ? 0 : -1}
                      variant="secondary"
                    >
                      Close pack opening
                    </Button>
                  ) : null}
                </div>
              </div>
              {cashError ? (
                <p role="alert">
                  Cash-out failed. Your horse is still in your stable.
                </p>
              ) : null}
            </div>
            <span className="quick-rip__fairness">
              <ShieldCheck aria-hidden />
              Contents were fixed by the committed series before this
              presentation ran.
            </span>
          </div>
        </section>
      </main>
    </div>
  );
}

function HorseRipFace({
  horse,
  tier,
  upgrading,
  final,
}: {
  horse: PackHorse;
  tier: RipTier;
  upgrading: boolean;
  final: boolean;
}) {
  return (
    <article className="rip-card__face">
      <header>
        <span className={`tier-chip tier-chip--${tier}`}>{tier}</span>
        <TierStars rating={final ? horse.starRating : undefined} tier={tier} />
      </header>
      <HorsePortrait horse={horse} />
      <div className="rip-card__identity">
        <strong>{horse.name}</strong>
        <small>
          {horse.bloodline ?? "World"} · {horse.archetype}
        </small>
      </div>
      <footer>
        <span>
          <strong>
            {final
              ? money(horse.instantQuoteCents ?? horse.stickerCents)
              : "•••"}
          </strong>
          <small>{final ? "INSTANT VALUE" : "VALUE LOCKED"}</small>
        </span>
        {upgrading ? (
          <b>
            <Zap aria-hidden /> UPGRADE
          </b>
        ) : null}
      </footer>
    </article>
  );
}
